import { DashboardEmissionBudgetType } from "@/features/Dashboard";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";

export type UsedEmission = {
  percentage: number;
  value: number;
};

/**
 * The emission figure a project is judged by.
 *
 * Under BR18 2025 a project with special conditions enabled is measured on its special-condition
 * total rather than the plain one, so the two are not interchangeable. Shared between the project
 * cards and the revision list: computing this in two places is how the same project ends up
 * showing two different numbers on two screens.
 */
export const getUsedEmission = (emissionBudget: DashboardEmissionBudgetType, specialConditionsEnabled: boolean): UsedEmission => {
  if (specialConditionsEnabled && emissionBudget.calculationType.variant?.variant === calculationTypeVariant.BR182025) {
    return emissionBudget.used.children.SpecialCondition;
  }

  return {
    percentage: emissionBudget.used.percentage,
    value: emissionBudget.used.value,
  };
};

/** The only part of a project that decides which of the two figures applies. */
type ProjectSpecialConditions = { specialConditionConfig?: { enabled?: boolean | null } | null };

/**
 * The emission figure a project is judged by, resolved from the project itself.
 *
 * Prefer this wherever a project object is at hand. `getUsedEmission` asks the caller to know that
 * special conditions are what select the figure, and three call sites did not: the project table,
 * the comparison view and the comparison's "best project" award all read `budget.used` straight and
 * so disagreed with the card they were opened from.
 */
export const getProjectUsedEmission = (
  project: ProjectSpecialConditions,
  emissionBudget: DashboardEmissionBudgetType
): UsedEmission => getUsedEmission(emissionBudget, !!project.specialConditionConfig?.enabled);

/**
 * The change from an older revision to a newer one, or null when there is nothing meaningful to
 * show: no older revision, either side missing a budget, or the two measured in different units
 * (a denominator change between save points makes the subtraction meaningless).
 */
export const getEmissionDelta = (
  current: { value: number; unitId: string } | null,
  previous: { value: number; unitId: string } | null
): number | null => {
  if (!current || !previous) return null;
  if (current.unitId !== previous.unitId) return null;

  const delta = current.value - previous.value;

  // Anything below this rounds to "0" once formatted, and "+0" reads as a change that did not
  // happen. Matches formatNumber's 2-decimal presentation.
  return Math.abs(delta) < 0.005 ? null : delta;
};
